/**
 * Moldura do app: cabeçalho com navegação HORIZONTAL (uma entrada por seção, com as
 * páginas num menu suspenso), como o menu no topo do legado. Em telas estreitas, a
 * navegação vai para uma gaveta aberta pelo botão ☰.
 */
import {
  AppShell,
  Burger,
  Button,
  Drawer,
  Group,
  Image,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Outlet, useLocation } from 'react-router';

import { sair, type Usuario } from '../api/sessao';
import { useRegistrarVisitas } from '../lib/recentes';
import { DialogoLimparCache } from './LimparCache';
import { LimiteDeErro } from './LimiteDeErro';
import { porSecao, rotaNova, type Pagina } from './paginas';

function ItemPagina({ pagina, onEscolher }: { pagina: Pagina; onEscolher?: () => void }) {
  return (
    <Menu.Item component={Link} to={rotaNova(pagina)} onClick={onEscolher}>
      <Text size="sm">{pagina.titulo}</Text>
    </Menu.Item>
  );
}

function secaoAtiva(paginas: Pagina[], pathname: string) {
  return paginas.some((p) => pathname.startsWith(rotaNova(p)));
}

function MenuHorizontal({ pathname }: { pathname: string }) {
  return (
    <Group gap={0} wrap="nowrap" visibleFrom="lg">
      <Button
        component={Link}
        to="/"
        variant={pathname === '/' ? 'light' : 'subtle'}
        size="compact-sm"
        px={8}
        color="persevera"
      >
        Início
      </Button>
      {porSecao().map(([secao, paginas]) => (
        <Menu
          key={secao}
          trigger="click-hover"
          openDelay={80}
          closeDelay={150}
          position="bottom-start"
          shadow="md"
          width={300}
        >
          <Menu.Target>
            <Button
              variant={secaoAtiva(paginas, pathname) ? 'light' : 'subtle'}
              size="compact-sm"
              px={8}
              color="persevera"
            >
              {secao}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {paginas.map((p) => (
              <ItemPagina key={p.slugLegado} pagina={p} />
            ))}
          </Menu.Dropdown>
        </Menu>
      ))}
    </Group>
  );
}

function NavegacaoCompacta({ pathname, onEscolher }: { pathname: string; onEscolher: () => void }) {
  return (
    <ScrollArea>
      <NavLink
        component={Link}
        to="/"
        label="Início"
        active={pathname === '/'}
        onClick={onEscolher}
      />
      {porSecao().map(([secao, paginas]) => (
        <NavLink key={secao} label={secao} defaultOpened={secaoAtiva(paginas, pathname)}>
          {paginas.map((p) => (
            <NavLink
              key={p.slugLegado}
              component={Link}
              to={rotaNova(p)}
              label={p.titulo}
              active={pathname === rotaNova(p)}
              onClick={onEscolher}
            />
          ))}
        </NavLink>
      ))}
    </ScrollArea>
  );
}

export function Shell({ usuario }: { usuario: Usuario }) {
  const [gaveta, { toggle, close }] = useDisclosure();
  const [limparCache, { open: abrirLimparCache, close: fecharLimparCache }] = useDisclosure(false);
  const { pathname } = useLocation();
  useRegistrarVisitas(usuario.email, pathname);

  return (
    <AppShell header={{ height: 60 }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="lg" wrap="nowrap">
            <Burger opened={gaveta} onClick={toggle} hiddenFrom="lg" size="sm" aria-label="Menu" />
            <UnstyledButton component={Link} to="/">
              <Image src="/logo_page.png" alt="Persevera" h={30} w="auto" fit="contain" />
            </UnstyledButton>
            <MenuHorizontal pathname={pathname} />
          </Group>
          <Menu position="bottom-end" shadow="md" width={260}>
            <Menu.Target>
              <Button
                variant="subtle"
                size="compact-sm"
                color="gray"
                rightSection={<span aria-hidden>▾</span>}
              >
                Conta
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{usuario.nome}</Menu.Label>
              <Menu.Item disabled>{usuario.email}</Menu.Item>
              <Menu.Divider />
              {usuario.is_superuser && (
                <Menu.Item onClick={abrirLimparCache}>Limpar cache</Menu.Item>
              )}
              <Menu.Item onClick={() => void sair()}>Sair</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>
      {usuario.is_superuser && (
        <DialogoLimparCache aberto={limparCache} onFechar={fecharLimparCache} />
      )}

      <Drawer opened={gaveta} onClose={close} title="Navegação" hiddenFrom="lg" size="xs">
        <NavegacaoCompacta pathname={pathname} onEscolher={close} />
      </Drawer>

      <AppShell.Main bg="#f2f2f2">
        {/* key: ao trocar de página, um erro anterior não fica preso na tela */}
        <LimiteDeErro key={pathname}>
          <Outlet />
        </LimiteDeErro>
      </AppShell.Main>
    </AppShell>
  );
}
